import type { NextPage } from "next"
import type { AppProps } from "next/app"
import Script from "next/script"
import { DefaultSeo } from "next-seo"
import type { ReactElement, ReactNode } from "react"

import "@/styles/globals.css"

import MaintenancePage from "@/components/common/MaintenancePage"

import { favicons } from "@/constants/favicons"
import { AppContext } from "@/context/AppContext"

export type GetPageLayout = {
  page: ReactElement
}

export type NextPageWithLayout = NextPage & {
  getPageLayout?: ({ page }: GetPageLayout) => ReactNode
}

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getPageLayout =
    Component.getPageLayout ||
    (({ page }) => {
      return page
    })

  // Show maintenance page if environment variable is set
  // Supports: "maintenance" or "rebuild"
  const maintenanceMode = process.env.NEXT_PUBLIC_MAINTENANCE_MODE
  if (maintenanceMode === `maintenance`) {
    return <MaintenancePage mode={`maintenance`} />
  }
  if (maintenanceMode === `rebuild`) {
    return <MaintenancePage mode={`rebuild`} />
  }

  return (
    <>
      <Script
        id={`google-tag-manager`}
        strategy={`afterInteractive`}
        dangerouslySetInnerHTML={{
          __html: `
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-WDXC7Q9');
          `,
        }}
      />
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}`}
        strategy={`afterInteractive`}
      />
      <Script id={`google-analytics`} strategy={`afterInteractive`}>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){window.dataLayer.push(arguments);}
          gtag('js', new Date());

          gtag('config', '${process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID}');
        `}
      </Script>
      <DefaultSeo
        titleTemplate={`%s | WellNow`}
        description={`WellNow offers last-minute health and wellness appointments in Christchurch across all leading clinics. Search, book and pay online in less than a minute.`}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_WELLNOW_HOST}`,
          site_name: `WellNow`,
          type: `website`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_WELLNOW_HOST}/images/wellnow-og-image.jpg`,
              width: 1200,
              height: 630,
              alt: `WellNow Logo`,
              type: `image/jpeg`,
            },
          ],
          locale: `en_NZ`,
        }}
        twitter={{
          handle: `@wellnownz`,
          site: `@wellnow.nz`,
          cardType: `summary_large_image`,
        }}
        additionalLinkTags={favicons}
      />
      <AppContext>
        {getPageLayout({ page: <Component {...pageProps} /> })}
      </AppContext>
    </>
  )
}

export default MyApp
